import { useCallback, useEffect, useRef, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { DateField } from '../../../../components/inputs/DateField';
import { FilesField, type FileUploadState } from '../../../../components/inputs/FilesField';
import { LazyRichTextField } from '../../../../components/inputs/LazyRichTextField';
import { Select } from '../../../../components/inputs/Select';
import type { SelectOption } from '../../../../components/inputs/selectShared';
import { TextField } from '../../../../components/inputs/TextField';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { useAttachmentImages } from '../../../../hooks/useAttachmentImages';
import { previewKindFor } from '../../../../hooks/useBlobPreview';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import { focusValidationError } from '../../../../lib/validationFocus';
import { casesApi } from '../../../case-management/cases/casesApi';
import { pressReleasesApi } from '../pressReleaseApi';
import {
  PRESS_RELEASE_BODY_MAX_TEXT,
  PRESS_RELEASE_FILE_ACCEPT,
  PRESS_RELEASE_FILE_HINT,
  PRESS_RELEASE_FILE_MAX_BYTES,
  PRESS_RELEASE_TITLE_MAX,
  pressReleaseLock,
  type PressReleaseAttachmentItem,
  type PressReleaseItem,
} from '../pressReleaseTypes';
import { fileAttachments } from '../utils/fileAttachments';
import { PENDING_PREFIX, pendingPicturesIn, placeUploadedPictures, uploadPendingPictures } from '../utils/pendingPictures';
import { samplePressRelease } from '../utils/samplePressRelease';

const ID_PREFIX = 'press-release';

/** What the content can draw. The server verifies the bytes; this only saves a pointless upload. */
const PICTURE_TYPES = ['image/png', 'image/jpeg'];

const FILES_DESCRIPTION =
  `Videos (MP4), PDFs and images (PNG or JPEG), up to ${formatBytes(PRESS_RELEASE_FILE_MAX_BYTES)} each. ` +
  "Each is filed into the Repository once the press release is saved, one at a time, and verified against the file's own leading bytes rather than its name.";

/**
 * Write a press release, or correct a draft — LARGE, because the content is a document written at
 * length in the rich-text editor.
 *
 * **A new release is always a DRAFT.** The form has no status: publishing is its own action, on the
 * profile, under its own key.
 *
 * **The files are filed AFTER the release, one request each** (Routing's shape): the release must
 * exist for them to belong to, and one file failing does not undo it. The files block shows on a
 * CREATE only, and only to a writer holding `attach`.
 *
 * **`section` makes it a profile card's DEDICATED edit** — `details` the title and release date,
 * `content` the document. The other values go back exactly as stored, so the write changes only what
 * the card shows. `all` is the create form and a list row's Edit.
 *
 * **Autogenerate RE-MOUNTS the editor with the sample**, by its `key` — mounting with a value is how
 * an edit opens over a stored document, so the sample is an honest write of a required field rather
 * than a push into a live editor (which would reset the caret).
 */
export function PressReleaseModal({
  editing,
  section = 'all',
  onClose,
  onSaved,
}: {
  editing: PressReleaseItem | null;
  section?: 'all' | 'details' | 'content';
  onClose: () => void;
  onSaved: (saved: PressReleaseItem) => void;
}) {
  const toast = useToast();
  const canAttach = usePermission('press-releases.attach');
  const canReferenceCase = usePermission('cases.read');

  const showDetails = section !== 'content';
  const showContent = section !== 'details';

  const [title, setTitle] = useState(editing?.title ?? '');
  const [releaseDate, setReleaseDate] = useState(editing?.releaseDate ?? '');
  const [body, setBody] = useState(editing?.body ?? '');
  const [caseId, setCaseId] = useState(editing?.caseId ?? '');
  const [bodyKey, setBodyKey] = useState(0);
  const [files, setFiles] = useState<File[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [uploads, setUploads] = useState<Record<number, FileUploadState> | undefined>(undefined);
  const [error, setError] = useState<ApiError | null>(null);
  const created = useRef<PressReleaseItem | null>(null);
  // A picture refused at pick time — the form is still on screen, so it is said in the form's strip.
  const [pickRefusal, setPickRefusal] = useState<string | null>(null);

  /*
    THE PICTURES THIS RELEASE ALREADY CARRIES, offered to the body.

    Only the ones the SERVER verified as images: the body renders a picture or nothing, so
    offering a PDF would promise something the document cannot draw. The list comes from the
    attachments the release already has rather than a second upload path — one set of bytes,
    one place to delete them from, and no new endpoint or file category.
  */
  const lock = editing ? pressReleaseLock(editing) : null;

  const [attachments, setAttachments] = useState<PressReleaseAttachmentItem[]>([]);

  /*
    THE CASES THIS WRITER CAN OPEN — the registry's own list, so membership and the level have
    already applied. A RESTRICTED row (listed, its title masked) is left out: a case whose title the
    writer may not read is not one they can recognise, and the server would refuse the link anyway.
    The stored reference is seeded from the case itself, or masked where the writer cannot open it.
  */
  const [caseOptions, setCaseOptions] = useState<SelectOption[] | null>(null);
  const [caseLoadFailed, setCaseLoadFailed] = useState(false);
  // Case Management is open to EMPLOYEES only — said on the field rather than an empty list.
  const [isEmployee, setIsEmployee] = useState(true);

  useEffect(() => {
    if (!canReferenceCase || !showDetails) return;

    const controller = new AbortController();
    const stored = editing?.caseId ?? null;

    void Promise.all([
      casesApi.eligibility(controller.signal),
      casesApi.list({ page: 1, pageSize: 100, status: 'active' }, controller.signal),
      stored ? casesApi.get(stored, controller.signal).catch(() => null) : Promise.resolve(null),
    ])
      .then(([eligibility, page, current]) => {
        if (controller.signal.aborted) return;

        setIsEmployee(eligibility.isEmployee);

        const options: SelectOption[] = page.items
          .filter((c) => !c.isRestricted)
          .map((c) => ({ value: c.id, label: c.title, sublabel: c.caseNumber, keywords: c.caseNumber }));

        if (stored && !options.some((o) => o.value === stored)) {
          options.unshift(
            current && !current.isRestricted
              ? { value: current.id, label: current.title, sublabel: current.caseNumber, keywords: current.caseNumber }
              : { value: stored, label: '********' },
          );
        }

        setCaseOptions(options);
      })
      .catch(() => {
        if (!controller.signal.aborted) setCaseLoadFailed(true);
      });

    return () => controller.abort();
  }, [canReferenceCase, showDetails, editing?.caseId]);

  useEffect(() => {
    if (!editing) return;

    let cancelled = false;

    void pressReleasesApi
      .attachments(editing.id, { page: 1, pageSize: 100 })
      .then((page) => {
        if (!cancelled) setAttachments(page.items.filter((a) => a.isActive));
      })
      // A failed list leaves the picker empty and says so; the document is still editable.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [editing]);

  const pictures = attachments.filter((a) => previewKindFor(a.contentType) === 'image');

  const loadPicture = useCallback(
    async (attachmentId: string) => {
      const downloaded = await pressReleasesApi.download(editing!.id, attachmentId);
      /*
        The TYPE comes from the attachment row, which carries what the SERVER verified on
        upload — never from the response, and never from the file name. That is the same rule
        `useBlobPreview` keeps, and it is what the re-typed blob depends on.
      */
      return {
        blob: downloaded.blob,
        contentType: pictures.find((p) => p.id === attachmentId)?.contentType ?? null,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the ids, not the array identity
    [editing?.id, pictures.map((p) => p.id).join(',')],
  );

  const storedPictureUrls = useAttachmentImages(
    pictures.map((p) => p.id),
    loadPicture,
  );

  /*
    PICTURES PLACED FROM THE COMPUTER, held here until Submit. Each is drawn from the reader's own
    file through a local object URL, and uploaded only if the form is submitted — Cancel leaves
    nothing on the server. `uploaded` maps a pending id to the attachment it became, and outlives a
    failed submit so a retry never files a picture twice.
  */
  const [pending, setPending] = useState<Record<string, { file: File; url: string }>>({});
  const pendingRef = useRef(pending);
  const nextPending = useRef(1);
  const uploaded = useRef<Record<string, string>>({});

  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  // The reader's own files, in their own tab — released when the form goes.
  useEffect(() => () => Object.values(pendingRef.current).forEach((p) => URL.revokeObjectURL(p.url)), []);

  function pickPicture(file: File): string | null {
    if (!PICTURE_TYPES.includes(file.type)) {
      setPickRefusal(`${file.name} is not a PNG or JPEG picture, so it cannot be placed in the content.`);
      return null;
    }

    if (file.size > PRESS_RELEASE_FILE_MAX_BYTES) {
      setPickRefusal(`${file.name} is larger than ${formatBytes(PRESS_RELEASE_FILE_MAX_BYTES)}, so it cannot be placed in the content.`);
      return null;
    }

    setPickRefusal(null);
    const pendingId = `${PENDING_PREFIX}${nextPending.current++}`;
    const url = URL.createObjectURL(file);

    setPending((current) => ({ ...current, [pendingId]: { file, url } }));
    return pendingId;
  }

  const pictureUrls = {
    ...storedPictureUrls,
    ...Object.fromEntries(Object.entries(pending).map(([pendingId, p]) => [pendingId, p.url])),
  };

  const showFiles = editing === null && canAttach;
  const oversize = files.some((file) => file.size > PRESS_RELEASE_FILE_MAX_BYTES);
  const canSave =
    title.trim().length > 0 && releaseDate.length > 0 && body.length > 0 && !oversize && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const request = { title: title.trim(), releaseDate, caseId: caseId || null };
      const placed = pendingPicturesIn(body);
      const pendingFiles = Object.fromEntries(Object.entries(pending).map(([pendingId, p]) => [pendingId, p.file]));
      const failedPictures: { name: string; reason: string }[] = [];

      let saved: PressReleaseItem;

      /*
        A pending id NEVER reaches the server. Editing, the release exists, so the pictures go up
        FIRST and the content is written once with their real ids. Adding, there is nothing to file
        them against yet: the release is written WITHOUT them, they go up, and the content is
        written again with them placed. If that last write fails, the release stands without the
        pictures in its text and they are on its attachments list — degraded, never broken.
      */
      const target = editing ?? created.current;

      if (target) {
        if (placed.length > 0) setProgress(0);
        failedPictures.push(
          ...(await uploadPendingPictures(target.id, placed, pendingFiles, uploaded.current, setProgress)),
        );
        setProgress(null);

        saved = await pressReleasesApi.update(target.id, { ...request, body: placeUploadedPictures(body, uploaded.current) });
      } else {
        saved = await pressReleasesApi.create({ ...request, body: placeUploadedPictures(body, {}) });
        // Held, so a resubmit after a later failure corrects this release rather than adding another.
        created.current = saved;

        if (placed.length > 0) {
          setProgress(0);
          failedPictures.push(
            ...(await uploadPendingPictures(saved.id, placed, pendingFiles, uploaded.current, setProgress)),
          );
          setProgress(null);

          if (Object.keys(uploaded.current).length > 0) {
            try {
              saved = await pressReleasesApi.update(saved.id, { ...request, body: placeUploadedPictures(body, uploaded.current) });
            } catch {
              toast.failure(
                `"${saved.title}" was added, and its pictures were uploaded to its attachments, but they could not be placed in the content. Open the release and edit its content to place them.`,
              );
            }
          }
        }
      }

      if (failedPictures.length > 0) {
        toast.failure(
          `${failedPictures.map((f) => `${f.name} (${f.reason})`).join('; ')} could not be uploaded, so ${failedPictures.length === 1 ? 'it was' : 'they were'} taken out of the content.`,
        );
      }

      if (showFiles && files.length > 0) {
        setProgress(0);
        const failed = await fileAttachments(saved.id, files, setProgress, (index, state) =>
          setUploads((current) => ({ ...current, [index]: state })),
        );

        if (failed.length > 0) {
          toast.failure(
            `The press release was saved, but ${failed.map((f) => `${f.name} (${f.reason})`).join('; ')} could not be filed. Attach ${failed.length === 1 ? 'it' : 'them'} again from the press release.`,
          );
        }
      }

      toast.success(editing ? `Saved "${saved.title}".` : `Added "${saved.title}" as a draft.`);
      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save this press release.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID_PREFIX });
    } finally {
      setSaving(false);
      setProgress(null);
    }
  }

  // Fills only the fields this section SHOWS — never one the writer cannot see being changed.
  function generate() {
    const sample = samplePressRelease();

    if (showDetails) {
      setTitle(sample.title);
      setReleaseDate(sample.releaseDate);
      // Optional, and read off the LIVE list — a case this writer can open, or none.
      if (caseOptions && caseOptions.length > 0) setCaseId(caseOptions[0].value);
    }

    if (showContent) {
      setBody(sample.body);
      setBodyKey((k) => k + 1);
    }
  }

  const heading = !editing
    ? 'Add press release'
    : section === 'details'
      ? `Edit details of ${editing.title}`
      : section === 'content'
        ? `Edit the content of ${editing.title}`
        : `Edit ${editing.title}`;

  return (
    <Modal
      open
      onClose={onClose}
      title={heading}
      size="3xl"
      id={`${ID_PREFIX}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{ id: `${ID_PREFIX}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${ID_PREFIX}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel={progress === null ? 'Submitting' : `Filing ${progress}%`}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {pickRefusal && !error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{pickRefusal}</p>
          </div>
        ) : null}

        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {showDetails ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id={`${ID_PREFIX}-title`}
              label="Title"
              required
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={PRESS_RELEASE_TITLE_MAX}
              disabled={saving}
              error={error?.fieldError('title')}
            />

            <DateField
              id={`${ID_PREFIX}-release-date`}
              label="Release date"
              description="The day the press release goes out."
              required
              value={releaseDate}
              onChange={setReleaseDate}
              disabled={saving}
              error={error?.fieldError('releaseDate')}
            />

            {canReferenceCase ? (
              <Select
                id={`${ID_PREFIX}-case-id`}
                label="Case"
                description={
                  caseLoadFailed
                    ? 'The cases could not be loaded. Close the form and try again.'
                    : !isEmployee
                      ? "Case Management is open only to employees, and your account is not linked to an employee's personnel record — so there is no case you can reference."
                      : "The case this press release is about. Its Post Operation Reports then show on the press release's own tab — to readers who can open the case."
                }
                placeholder="Search by case title or number…"
                value={caseId}
                onChange={setCaseId}
                options={caseOptions ?? []}
                loading={caseOptions === null && !caseLoadFailed}
                remote
                disabled={saving || !isEmployee}
                error={error?.fieldError('caseId')}
              />
            ) : null}
          </div>
        ) : null}

        {showContent ? (
          <LazyRichTextField
            key={bodyKey}
            id={`${ID_PREFIX}-body`}
            label="Content"
            description="The press release itself — headings, lists and tables are kept."
            required
            value={body}
            /*
              A picture is placed straight from the computer — by the button, a paste or a drop —
              and uploaded on Submit. Only while the release is writable and the reader may attach:
              the attach endpoint refuses a published release, so offering the control there would
              promise an action the server is about to refuse. The lock reaches paste and drop too,
              because the editor reads the same reason for all three.
            */
            images={{
              options: pictures.map((p) => ({ id: p.id, label: p.fileName })),
              urls: pictureUrls,
              // A PERMISSION hides the upload path; only the lock (STATE) disables, with its reason.
              pick: canAttach ? pickPicture : undefined,
              accept: PICTURE_TYPES.join(','),
              disabledReason: lock,
            }}
            onChange={setBody}
            maxLength={PRESS_RELEASE_BODY_MAX_TEXT}
            disabled={saving}
            error={error?.fieldError('body')}
          />
        ) : null}

        {showFiles ? (
          <FilesField
            id={`${ID_PREFIX}-files`}
            label="Attachments"
            description={FILES_DESCRIPTION}
            hint={PRESS_RELEASE_FILE_HINT}
            accept={PRESS_RELEASE_FILE_ACCEPT}
            maxBytes={PRESS_RELEASE_FILE_MAX_BYTES}
            files={files}
            onChange={setFiles}
            uploads={uploads}
            disabled={saving}
          />
        ) : null}
      </div>
    </Modal>
  );
}
